import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Send, ArrowUpRight, Plus, Bell, Users, FileSignature, TrendingUp, ArrowDown, CalendarClock, FileText } from "lucide-react";
import DashboardSectionCard from "@/components/dashboard/DashboardSectionCard";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const RECENT_MS = 24 * 60 * 60 * 1000;

function suggestionsFor({ playerId, firstName, signals, tf }) {
  if (playerId) {
    const list = [
      { icon: Bell, text: `${tf("Que dois-je faire pour")} ${firstName} ${tf("aujourd'hui ?")}`, w: 1 },
      { icon: CalendarClock, text: `${tf("Quelle est ma prochaine échéance avec")} ${firstName} ?`, w: signals?.nextDeadline ? 3 : 0 },
      { icon: FileText, text: tf("Quels documents dois-je vérifier ?"), w: (signals?.docsCount || 0) > 0 ? 2 : 0 },
      { icon: FileSignature, text: tf("Où en est sa situation contractuelle ?"), w: 0 },
    ];
    return list.sort((a, b) => b.w - a.w).slice(0, 4);
  }
  const list = [
    { icon: Bell, text: tf("Qui dois-je relancer aujourd'hui ?"), w: (signals?.relancer || 0) > 0 ? 3 : 0 },
    { icon: Users, text: tf("Quels athlètes demandent mon attention ?"), w: 1 },
    { icon: FileText, text: tf("Quels contrats arrivent bientôt à échéance ?"), w: (signals?.docsCount || 0) > 0 ? 2 : 0 },
    { icon: TrendingUp, text: tf("Que dois-je prioriser cette semaine ?"), w: 1 },
  ];
  return list.sort((a, b) => b.w - a.w).slice(0, 4);
}

function AssistantAnswer({ r, onFollowUp, tf }) {
  return (
    <div className="space-y-2">
      {r.degraded && (
        <p className="text-[10px] text-amber-500">{tf("Une partie de l'analyse n'a pas pu être générée. Vos données Athlynx restent disponibles.")}</p>
      )}
      <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{r.summary}</p>
      {(r.entities || []).slice(0, 5).map((e, i) => (
        <div key={i} className="rounded-lg border border-border bg-secondary/30 px-2.5 py-2" data-testid={`dash-intel-entity-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold truncate">{e.name}</p>
            {e.link && (
              <Link to={e.link} className="text-[11px] text-athlynx hover:underline inline-flex items-center gap-0.5 shrink-0">
                {tf("Ouvrir")} <ArrowUpRight className="h-3 w-3" />
              </Link>
            )}
          </div>
          {(e.signals || []).slice(0, 2).map((s, j) => (
            <p key={j} className="text-[11px] text-muted-foreground mt-0.5 flex items-start gap-1.5">
              <span className="h-1 w-1 rounded-full bg-athlynx mt-1.5 shrink-0" />{s}
            </p>
          ))}
          {e.recommended_action && (
            <p className="text-[11px] mt-1 text-foreground/80"><span className="font-medium">{tf("Action recommandée")} :</span> {e.recommended_action}</p>
          )}
        </div>
      ))}
      {(r.follow_up_questions || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {r.follow_up_questions.slice(0, 3).map((q, i) => (
            <button key={i} type="button" onClick={() => onFollowUp(q)} data-testid={`dash-intel-followup-${i}`}
              className="text-[10px] text-muted-foreground border border-border rounded-full px-2 py-0.5 hover:border-athlynx/40 hover:text-foreground transition-colors motion-reduce:transition-none">
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardIntelligenceCard({ playerId, athleteName, signals }) {
  const { tf, lang } = useLang();
  const navigate = useNavigate();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const scrollRef = useRef(null);
  const ctxRef = useRef(playerId);
  const firstName = (athleteName || "").split(" ")[0];

  const resetToDiscovery = useCallback(() => {
    setConvId(null); setMessages([]); setFailed(null); setInput("");
  }, []);

  // Restore the most recent dashboard conversation for the CURRENT context only.
  useEffect(() => {
    ctxRef.current = playerId;
    resetToDiscovery();
    let alive = true;
    const params = { origin: "dashboard", ...(playerId ? { player_id: playerId } : { context: "portfolio" }) };
    api.get("/intelligence/conversations", { params }).then(async (r) => {
      if (!alive || ctxRef.current !== playerId) return;
      const latest = (r.data.conversations || [])[0];
      if (!latest || Date.now() - new Date(latest.updated_at).getTime() > RECENT_MS) return;
      const c = await api.get(`/intelligence/conversations/${latest.id}`).catch(() => null);
      if (!alive || ctxRef.current !== playerId || !c) return;
      setConvId(latest.id);
      setMessages((c.data.messages || []).map((m) => ({ role: m.role, content: m.content, structured: m.structured_response || null })));
    }).catch(() => {});
    return () => { alive = false; };
  }, [playerId, resetToDiscovery]);

  const nearBottom = () => {
    const el = scrollRef.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };
  const scrollBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    setShowJump(false);
  };
  useEffect(() => {
    if (!messages.length) return;
    if (nearBottom()) requestAnimationFrame(() => scrollBottom(false));
    else setShowJump(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, asking]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || asking) return;
    const pid = playerId;
    setInput(""); setFailed(null); setAsking(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    try {
      const r = await api.post("/intelligence/query", {
        message, conversation_id: convId, player_id: pid || null,
        origin: "dashboard", scope: tf("Vue d'ensemble"), lang,
      });
      if (ctxRef.current !== pid) return;
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.response?.summary || "", structured: r.data.response }]);
    } catch {
      if (ctxRef.current !== pid) return;
      setFailed(message);
    } finally {
      if (ctxRef.current === pid) setAsking(false);
    }
  };

  const active = messages.length > 0 || asking;
  const suggestions = useMemo(
    () => suggestionsFor({ playerId, firstName, signals, tf }),
    [playerId, firstName, signals, tf]
  );

  const headerRight = (
    <TooltipProvider delayDuration={200}>
      {active && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={resetToDiscovery} aria-label={tf("Nouvelle conversation")} data-testid="dash-intel-new-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">{tf("Nouvelle conversation")}</TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
            onClick={() => navigate(convId ? `/intelligence?conversation=${convId}` : "/intelligence")}
            aria-label={tf("Voir dans Athlynx Intelligence")} data-testid="dash-intel-open-btn">
            {tf("Ouvrir")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">{tf("Voir dans Athlynx Intelligence")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const composer = (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => { e.preventDefault(); send(); }}
    >
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={active ? tf("Demander à Athlynx…") : tf("Demander à Athlynx Intelligence…")}
        aria-label={tf("Demander à Athlynx Intelligence…")}
        data-testid="dash-intel-input"
        className="flex-1 min-w-0 h-9 rounded-lg border border-border bg-secondary/40 px-3 text-[13px] placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <Button type="submit" size="sm" disabled={!input.trim() || asking} aria-label={tf("Envoyer")}
        className="h-9 w-9 p-0 rounded-lg bg-athlynx hover:bg-athlynx-hover text-white shrink-0" data-testid="dash-intel-submit">
        <Send className="h-4 w-4" />
      </Button>
    </form>
  );

  return (
    <DashboardSectionCard
      icon={Sparkles}
      iconClass="text-athlynx"
      title={playerId && firstName ? <>Athlynx Intelligence <span className="text-muted-foreground/70 normal-case tracking-normal">· {firstName}</span></> : "Athlynx Intelligence"}
      right={headerRight}
      testid="card-dashboard-intelligence"
      className="lg:min-h-[520px] xl:min-h-0 xl:h-[clamp(470px,31vw,570px)]"
      contentClassName="relative"
      footer={composer}
    >
      {!active ? (
        <div className="flex-1 min-h-0 flex flex-col animate-fade-in motion-reduce:animate-none" data-testid="dash-intel-discovery">
          <h3 className="text-base font-semibold tracking-tight">{tf("Que souhaitez-vous savoir ?")}</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            {playerId
              ? `${tf("Interrogez les données de")} ${firstName} ${tf("présentes dans Athlynx.")}`
              : tf("Interrogez votre portefeuille à partir des données présentes dans Athlynx.")}
          </p>
          <div className="space-y-2">
            {suggestions.map((s, i) => {
              const Icon = s.icon;
              return (
                <button key={i} type="button" onClick={() => send(s.text)} data-testid={`dash-intel-suggestion-${i}`}
                  className="w-full flex items-center gap-2.5 rounded-xl border border-border bg-secondary/30 px-3 py-2.5 text-left text-[13px] hover:border-athlynx/35 hover:bg-secondary/60 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                  <Icon className="h-3.5 w-3.5 text-athlynx shrink-0" />
                  <span className="truncate">{s.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div ref={scrollRef} onScroll={() => { if (nearBottom()) setShowJump(false); }}
            className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3 animate-fade-in motion-reduce:animate-none"
            data-testid="dash-intel-transcript" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i}>
                {m.role === "user" ? (
                  <div data-testid="dash-intel-user-msg">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">{tf("Vous")}</p>
                    <p className="text-[13px] font-medium mt-0.5">{m.content}</p>
                  </div>
                ) : (
                  <div data-testid="dash-intel-assistant-msg">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-athlynx flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Athlynx
                    </p>
                    <div className="mt-1">
                      {m.structured ? <AssistantAnswer r={m.structured} onFollowUp={send} tf={tf} /> : <p className="text-[13px]">{m.content}</p>}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {asking && (
              <div data-testid="dash-intel-loading">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-athlynx flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Athlynx
                </p>
                <p className="text-[13px] text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                  {playerId ? `${tf("Analyse des données de")} ${firstName}…` : tf("Analyse de votre portefeuille…")}
                  <span className="inline-flex gap-0.5" aria-hidden>
                    <span className="h-1 w-1 rounded-full bg-athlynx animate-pulse motion-reduce:animate-none" />
                    <span className="h-1 w-1 rounded-full bg-athlynx animate-pulse motion-reduce:animate-none [animation-delay:150ms]" />
                    <span className="h-1 w-1 rounded-full bg-athlynx animate-pulse motion-reduce:animate-none [animation-delay:300ms]" />
                  </span>
                </p>
              </div>
            )}
            {failed && (
              <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="dash-intel-error">
                <p className="text-xs text-muted-foreground">{tf("Athlynx Intelligence est momentanément indisponible.")}</p>
                <button type="button" onClick={() => { setMessages((m) => m.slice(0, -1)); send(failed); }}
                  className="text-xs text-athlynx font-medium hover:underline mt-1" data-testid="dash-intel-retry">
                  {tf("Réessayer")}
                </button>
              </div>
            )}
          </div>
          {showJump && (
            <button type="button" onClick={() => scrollBottom()} data-testid="dash-intel-jump-btn"
              className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full border border-border bg-card shadow-sm px-2.5 py-1 text-[11px] text-foreground hover:border-athlynx/40 transition-colors motion-reduce:transition-none">
              <ArrowDown className="h-3 w-3" /> {tf("Nouveau message")}
            </button>
          )}
        </>
      )}
    </DashboardSectionCard>
  );
}
